/**
 * Batched creation of file index records (reliability plan 6.2, site half).
 *
 * Every file a Files-page batch stores gets a `src::<storage path>` record
 * before its indexing is requested: the passes reference it and cannot create
 * it reliably themselves. That used to be one `postRecord` per file. With
 * skapi-js's `postRecords` (one request, up to 100 records, per-item results)
 * the records of a batch are written in batches of 100 instead, and a refused
 * item is reported for that one file while the rest proceed.
 *
 * Three parts, all DOM-free and framework-free so they are tested on their own:
 *
 *   - fileIndexRecordItem: THE shape of the record, shared with the per-file
 *     path (ai_agent.ts's ensureFileIndexRecord builds its postRecord from it),
 *     so the two can never drift. The duplicate check and the page-text badge
 *     read `size_bytes` and `indexed_at` off it.
 *   - RecordBatcher: collects items and posts them 100 at a time, or when the
 *     stream goes quiet, or when the oldest item has waited long enough. Each
 *     add() is a promise for its own record.
 *   - the queue: the Files page queues a record the moment a file's bytes are
 *     stored (outside the index set-up pool, which is what lets a batch fill),
 *     and the file's set-up takes it back and waits for it where it used to
 *     write the record itself. Behind a runtime feature check: without
 *     `postRecords` nothing is queued and the set-up writes per file as today.
 */

/** One item of postRecords(): the two arguments postRecord() takes, as one object. */
export type PostRecordsItem = { data?: object | null; config: { [key: string]: any } };
export type PostRecordsFailure = { index: number; code: string; message: string };
export type PostRecordsResult = { list: any[]; failed: PostRecordsFailure[] };
export type RecordPoster = (items: PostRecordsItem[]) => Promise<PostRecordsResult>;

/** Records per request. The SDK chunks at the same number; the batcher never
 *  hands it more than one chunk, so the per-item indices stay simple. */
export const POST_RECORDS_BATCH_SIZE = 100;

export const FILE_INDEX_RECORD_TABLE = "file_summaries";
export const FILE_INDEX_RECORD_NOTE =
    "File-level record created at upload. The indexing agent enriches this with sheet names, column headers and row counts.";

/**
 * The file index record as the per-file path writes it, as a postRecords item.
 * `accessGroup` is the value the record takes (already normalized by the
 * caller); `now` stamps `indexed_at`.
 */
export function fileIndexRecordItem(
    service: string,
    storagePath: string,
    meta: { name?: string; mime?: string; size?: number } | undefined,
    accessGroup: string | number,
    now: number = Date.now(),
): PostRecordsItem {
    return {
        data: null,
        config: {
            service,
            unique_id: "src::" + storagePath,
            table: { name: FILE_INDEX_RECORD_TABLE, access_group: accessGroup },
            // Deleting this record must take every record extracted from the file
            // with it: the backend's cascade follows references only when the
            // referenced record carries this flag. See ensureFileIndexRecord.
            source: { can_remove_referencing_records: true },
            data: {
                file_name: meta?.name || storagePath.split("/").pop() || storagePath,
                storage_path: storagePath,
                mime_type: meta?.mime || null,
                size_bytes: typeof meta?.size === "number" ? meta.size : null,
                indexed_at: now,
                note: FILE_INDEX_RECORD_NOTE,
            },
        },
    };
}

/** The backend's answer for a unique_id that already has a record. For a record
 *  whose job is to EXIST, that is success. */
export const isAlreadyTakenMessage = (message: unknown): boolean => /already taken/i.test(String(message || ""));

/** What an add() rejects with for an item the backend refused. */
export class RecordRefusedError extends Error {
    code: string;
    index: number;
    constructor(failure: PostRecordsFailure) {
        super(failure.message || failure.code || "The record was refused.");
        this.name = "RecordRefusedError";
        this.code = failure.code || "";
        this.index = failure.index;
    }
}

type Waiter = { item: PostRecordsItem; resolve: (rec: any) => void; reject: (err: any) => void; addedAt: number };

export type RecordBatcherOptions = {
    /** Items per request. Default POST_RECORDS_BATCH_SIZE. */
    size?: number;
    /** Flush this long after the last add. */
    idleMs?: number;
    /** Flush when the oldest item has waited this long, however busy the stream. */
    maxWaitMs?: number;
    now?: () => number;
    schedule?: (fn: () => void, ms: number) => unknown;
    cancel?: (handle: unknown) => void;
};

/**
 * Collects records and posts them in batches. add() resolves with the saved
 * record, or rejects with a RecordRefusedError for an item the backend refused,
 * or with the request's own error when the whole request failed (the caller's
 * retry then goes the per-file way).
 */
export class RecordBatcher {
    private post: RecordPoster;
    private size: number;
    private idleMs: number;
    private maxWaitMs: number;
    private now: () => number;
    private schedule: (fn: () => void, ms: number) => unknown;
    private cancel: (handle: unknown) => void;
    private waiting: Waiter[] = [];
    private timer: unknown = null;
    private inFlight: Promise<void> | null = null;
    /** Requests made so far. Diagnostics and tests. */
    requests = 0;

    constructor(post: RecordPoster, opts: RecordBatcherOptions = {}) {
        this.post = post;
        this.size = Math.max(1, Math.floor(opts.size || POST_RECORDS_BATCH_SIZE));
        this.idleMs = opts.idleMs ?? 250;
        this.maxWaitMs = opts.maxWaitMs ?? 1500;
        this.now = opts.now || Date.now;
        // A plain timer on purpose: callers await the flush it leads to, so it
        // must keep a Node process alive the short while it is armed.
        this.schedule = opts.schedule || ((fn, ms) => setTimeout(fn, ms));
        this.cancel = opts.cancel || ((h) => clearTimeout(h as any));
    }

    get pending(): number { return this.waiting.length; }

    add(item: PostRecordsItem): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.waiting.push({ item, resolve, reject, addedAt: this.now() });
            if (this.waiting.length >= this.size) {
                void this.flush();
                return;
            }
            this.arm();
        });
    }

    /** Re-arm the idle timer, bounded by the oldest item's maximum wait. */
    private arm(): void {
        if (this.timer !== null) this.cancel(this.timer);
        const oldest = this.waiting.length ? this.waiting[0].addedAt : this.now();
        const untilMax = Math.max(0, this.maxWaitMs - (this.now() - oldest));
        this.timer = this.schedule(() => { this.timer = null; void this.flush(); }, Math.min(this.idleMs, untilMax));
    }

    /** Post everything waiting, one request per `size` items, in order. */
    async flush(): Promise<void> {
        if (this.timer !== null) { this.cancel(this.timer); this.timer = null; }
        // One request at a time, so results map onto the right waiters and the
        // backend sees batches rather than a burst.
        if (this.inFlight) {
            await this.inFlight;
            if (!this.waiting.length) return;
        }
        const run = (async () => {
            while (this.waiting.length) {
                const chunk = this.waiting.splice(0, this.size);
                this.requests++;
                let result: PostRecordsResult;
                try {
                    result = await this.post(chunk.map((w) => w.item));
                } catch (err) {
                    for (const w of chunk) w.reject(err);
                    continue;
                }
                const failedAt = new Map<number, PostRecordsFailure>();
                for (const f of (result && Array.isArray(result.failed) ? result.failed : [])) failedAt.set(f.index, f);
                const saved = result && Array.isArray(result.list) ? result.list : [];
                let next = 0;
                chunk.forEach((w, i) => {
                    const f = failedAt.get(i);
                    if (f) w.reject(new RecordRefusedError(f));
                    else w.resolve(saved[next++]);
                });
            }
        })();
        this.inFlight = run;
        try { await run; } finally { if (this.inFlight === run) this.inFlight = null; }
    }
}

// ─── the Files page's queue of file index records ────────────────────────────

export type FileIndexBatchDeps = {
    /** skapi.postRecords, bound. */
    post: RecordPoster;
    /** The runtime feature check: `typeof skapi.postRecords === 'function'`.
     *  False means nothing is queued and the set-up writes per file as before. */
    available: () => boolean;
    batcher?: RecordBatcherOptions;
};

/** What the set-up takes back for a file whose record was queued. */
export type QueuedFileIndexRecord<G = string> = {
    /** The group the record was written at, the one the indexing prompt must use. */
    accessGroup: Promise<G>;
    /** Settles when the record exists: saved now, or already there. Rejects
     *  with the backend's refusal (RecordRefusedError) or the request's error. */
    done: Promise<void>;
};

let deps: FileIndexBatchDeps | null = null;
let batcher: RecordBatcher | null = null;
const queued = new Map<string, QueuedFileIndexRecord<any>>();
/** More than this many untaken entries means set-ups are not taking them; the
 *  oldest go, their records are written all the same. */
const QUEUE_MAX = 10_000;

export function configureFileIndexBatch(next: FileIndexBatchDeps | null): void {
    deps = next;
    batcher = next ? new RecordBatcher(next.post, next.batcher) : null;
    queued.clear();
}

export const fileIndexBatchAvailable = (): boolean => !!deps && !!batcher && deps.available();

const queueKey = (service: string, storagePath: string) => service + "|" + storagePath;

/**
 * Queue a file's record. `accessGroup` may still be being resolved (a project
 * set to ask settles its settings first); `normalize` turns the chosen group
 * into the value the record takes. No-op when the SDK has no postRecords.
 */
export function queueFileIndexRecord<G>(
    service: string,
    storagePath: string,
    meta: { name?: string; mime?: string; size?: number } | undefined,
    accessGroup: Promise<G> | G,
    normalize: (group: G) => string | number,
    now: number = Date.now(),
): boolean {
    if (!service || !storagePath || !fileIndexBatchAvailable()) return false;
    const group = Promise.resolve(accessGroup);
    const done = group
        .then((g) => batcher!.add(fileIndexRecordItem(service, storagePath, meta, normalize(g), now)))
        .then(
            () => undefined,
            (err) => {
                if (err instanceof RecordRefusedError && isAlreadyTakenMessage(err.message)) return undefined;
                throw err;
            },
        );
    // Nobody may ever take it (a set-up that returned early): the rejection
    // must not surface as unhandled. Takers attach their own handler.
    done.catch(() => {});
    group.catch(() => {});
    const key = queueKey(service, storagePath);
    queued.set(key, { accessGroup: group, done });
    if (queued.size > QUEUE_MAX) {
        const oldest = queued.keys().next().value;
        if (oldest !== undefined) queued.delete(oldest);
    }
    return true;
}

/** The set-up takes its file's queued record, once. Null when none was queued
 *  (an older SDK, a repair after a reload, an existing file). */
export function takeQueuedFileIndexRecord<G = string>(service: string, storagePath: string): QueuedFileIndexRecord<G> | null {
    const key = queueKey(service, storagePath);
    const entry = queued.get(key) as QueuedFileIndexRecord<G> | undefined;
    if (!entry) return null;
    queued.delete(key);
    return entry;
}

/** Post whatever is waiting now. */
export async function flushFileIndexRecords(): Promise<void> {
    if (batcher) await batcher.flush();
}

/** Diagnostics and tests. */
export const fileIndexBatchState = () => ({ queued: queued.size, waiting: batcher ? batcher.pending : 0, requests: batcher ? batcher.requests : 0 });
